/**
 * The work column of a case, the same on the page and in the Outlook pane: the draft
 * lines (a table or cards), the one action, and "Details (n)". The three steps a sales
 * person learns in the training: read the mail (the mail card, or Outlook itself),
 * check the draft, do the one action.
 *
 * `variant="page"`: the customer line sits in the mail card above, the draft is a
 * table, the action bar is pinned to the viewport bottom and the details sheet slides
 * in from the right. `variant="pane"`: the customer line comes first, the draft is a
 * list of cards, the bar sits inline, the sheet comes from the bottom, and "Open in
 * I2Q" sits next to "Details".
 *
 * Reads: the case (passed in), the newest reply draft, the cached quotation preview
 * (never asked for here; the sheet shows it, the one action never reads it). Writes go
 * through `useCaseWrites` to the app database only. Nothing is sent.
 *
 * The ease round (13 Oct 2026) wires here: a click on a blocker sentence goes to its
 * control (`goTo`), "Set quantity for all lines" has its dialog here, and the row keys
 * (Enter, `n`, `p`, `q`, `i`) act on the row of the draft that holds the focus.
 */

import { ExternalLink, Icon } from "@norican/icons";
import { Alert, Button, cn, useToastManager } from "@norican/react";
import { useMutationState } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";

import { openQuestionsOf, useRefreshOnRevision } from "@/features/case-review/answer-draft-panel";
import { BulkQuantityDialog } from "@/features/case-review/bulk-quantity-dialog";
import {
  type BlockerTarget,
  detailsCountOf,
  heldReasonOf,
} from "@/features/case-review/case-action";
import {
  CaseSheet,
  DetailsButton,
  SHEET_SECTIONS,
  type SheetSection,
} from "@/features/case-review/case-sheet";
import { stripCustomerOf, stripLinesOf } from "@/features/case-review/case-summary-strip";
import {
  caseAccountsOf,
  customerSearchStart,
  PickCustomerDialog,
} from "@/features/case-review/customer-card";
import { CustomerLine } from "@/features/case-review/customer-line";
import { CustomerParties } from "@/features/case-review/customer-parties";
import {
  currentDraftLineId,
  draftRowElement,
  focusDraftRow,
  focusNextRowAfterDecision,
  returnFocusToRow,
} from "@/features/case-review/draft-focus";
import { QTY_EDIT_ATTR } from "@/features/case-review/draft-line-cells";
import {
  type DraftRow,
  groupedRows,
  nextRowThatNeedsYou,
} from "@/features/case-review/draft-line-groups";
import { type DraftLayout, DraftLines } from "@/features/case-review/draft-lines";
import { InvestigationBar } from "@/features/case-review/investigation-panel";
import { AddLineDialog, LineCorrectionDialog } from "@/features/case-review/line-corrections";
import { PickItemDialog } from "@/features/case-review/line-decision";
import { hasLineMenu } from "@/features/case-review/line-menu";
import {
  effectiveItem,
  replyEligible,
  replyRevisionOf,
} from "@/features/case-review/line-semantics";
import { PrimaryAction } from "@/features/case-review/primary-action";
import { SetPriceDialog } from "@/features/case-review/set-price-dialog";
import { type CaseFlow } from "@/features/case-review/use-case-flow";
import {
  type CaseWrites,
  colleagueHasCase,
  useCaseWrites,
} from "@/features/case-review/use-case-writes";
import { useShortcutHandler } from "@/hooks/use-shortcuts";
import {
  answerDraftCreateKey,
  answerDraftFailureText,
  useAnswerDraft,
  useFoDraft,
  useMe,
} from "@/lib/api";
import { hasCapability } from "@/lib/areas";
import { FEATURES } from "@/lib/features";
import { isOutOfScopeCase } from "@/lib/intents";
import { logger } from "@/lib/logger";
import { type QuoteCaseDetail, type QuoteLine } from "@/lib/mock";
import { readRemembered, remember } from "@/lib/remembered";
import { ACTION_LABELS, customerChangedText, LINE_STATUS_WORDS } from "@/lib/sales-flow";
import { track } from "@/lib/usage";
import { useTrackOnce } from "@/lib/usage-react";

/** The browser key of the "Show details" switch. */
export const DETAILS_KEY = "i2q.case.details";

/**
 * "Show details": remembered, else off for everyone. Managers who may see the margin
 * started with it on, which filled the column with line 1's figures on first sight
 * (12 Oct 2026); the minimal view comes first, the switch is one click away.
 */
export function readDetailsOn(_maySeeMargin: boolean): boolean {
  return readRemembered(DETAILS_KEY, "off") === "on";
}

/** The sheet section named in the URL hash ("#checks"), or null. */
export function sectionFromHash(hash: string): SheetSection | null {
  const id = hash.replace(/^#/, "");
  return (SHEET_SECTIONS as readonly string[]).includes(id) ? (id as SheetSection) : null;
}

export interface CaseWorkProps {
  detail: QuoteCaseDetail;
  variant: "page" | "pane";
  /**
   * The draft as a table (a laptop) or as cards (a phone, the pane). The page passes
   * what its width says; the pane passes "list". Never decided by a media query alone,
   * so the server render and the tests get the same form.
   */
  layout?: DraftLayout;
  /** The walk through the queue; null in the pane and on a shared link. */
  flow?: CaseFlow | null;
  /** The pane's "Open in I2Q". */
  onOpenInI2q?: () => void;
  /**
   * The line a person is working on (the mail card marks its text), or null. `reveal`
   * is true for a click on the row's customer words: the mail then also scrolls the
   * marked text into view; a chevron or the action bar only marks it.
   */
  onFocusedLineChange?: (line: QuoteLine | null, reveal: boolean) => void;
  /**
   * The page shares its writes with the mail card (the customer line lives there), so
   * one "busy" and one error cover both; the pane makes its own.
   */
  writes?: CaseWrites;
  /** The page opens the customer search from the mail card: controlled from there. */
  pickingCustomer?: boolean;
  onPickingCustomerChange?: (open: boolean) => void;
  className?: string;
}

export function CaseWork({
  detail,
  variant,
  layout = "table",
  flow = null,
  onOpenInI2q,
  onFocusedLineChange,
  writes: sharedWrites,
  pickingCustomer: pickingCustomerProp,
  onPickingCustomerChange,
  className,
}: CaseWorkProps) {
  const { data: me } = useMe();
  const location = useLocation();
  const toast = useToastManager();
  const maySeeMargin = hasCapability(me, "see_margin");
  const mayApprove = hasCapability(me, "approve_price");
  const outOfScope = isOutOfScopeCase(detail);
  // A stand-in line (the whole mail, a signature) is not a line to review.
  const realLines = detail.lines.filter((line) => !line.fallback);
  const lines = stripLinesOf(realLines, detail);
  const customer = stripCustomerOf(detail);
  const ownWrites = useCaseWrites(detail);
  const writes = sharedWrites ?? ownWrites;

  // The newest reply draft; its freshness is read again when the case changes (F06).
  const eligible = replyEligible(detail);
  const answer = useAnswerDraft(detail.id, eligible);
  useRefreshOnRevision(detail.id, eligible ? replyRevisionOf(detail) : null);
  const draft = answer.data ?? null;
  const runs = useMutationState({
    filters: { mutationKey: answerDraftCreateKey(detail.id) },
    select: (mutation) => mutation.state,
  });
  const lastRun = runs[runs.length - 1];
  const preparing = runs.some((run) => run.status === "pending");
  const prepareError =
    lastRun?.status === "error" && lastRun.error ? answerDraftFailureText(lastRun.error) : null;
  // Read only from the cache: the work column never asks for the quotation preview
  // itself. The sheet shows it; the one action never reads its checks (they belong to
  // the step "Approve for the order system", not to the reply).
  const foDraft = useFoDraft(detail.id, false);
  // The rows as the draft lists them, for the dialogs and the row keys.
  const rows: DraftRow[] = outOfScope ? [] : groupedRows(realLines, detail.lines);

  const [detailsOn, setDetailsOn] = useState(() => readDetailsOn(maySeeMargin));
  const [pickLineId, setPickLineId] = useState<string | null>(null);
  const [priceLineId, setPriceLineId] = useState<string | null>(null);
  const [qtyLineId, setQtyLineId] = useState<string | null>(null);
  const [settingQuantities, setSettingQuantities] = useState(false);
  const [ownPickingCustomer, setOwnPickingCustomer] = useState(false);
  const pickingCustomer = pickingCustomerProp ?? ownPickingCustomer;
  const setPickingCustomer = onPickingCustomerChange ?? setOwnPickingCustomer;
  const [addingLine, setAddingLine] = useState(false);
  const [focusLineId, setFocusLineId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetSection, setSheetSection] = useState<SheetSection | null>(null);

  // A link into the sheet ("#checks", the training's price-approval link) opens it there.
  useEffect(() => {
    const section = sectionFromHash(location.hash);
    if (section) {
      track("case.details_open", { section });
      setSheetSection(section);
      setSheetOpen(true);
    }
  }, [location.hash]);

  // The usage log: the deeper search showed a proposal on this case, once per case.
  const proposedCustomers =
    Number(Boolean(detail.investigation?.customer)) +
    Number(Boolean(detail.investigation?.otherAccount));
  const proposedLines = detail.investigation?.lines.length ?? 0;
  useTrackOnce(
    "case.proposals_shown",
    { customer: proposedCustomers, lines: proposedLines },
    !outOfScope && proposedCustomers + proposedLines > 0 ? detail.id : null,
    { caseId: detail.id },
  );

  const openSheet = (section: SheetSection | null) => {
    logger.debug("[case] open details", { case: detail.id, section });
    track("case.details_open", { section: section ?? "none" }, { caseId: detail.id });
    setSheetSection(section);
    setSheetOpen(true);
  };
  // The chosen line: focused in the list, and its text marked in the mail (P28). A
  // click on the customer's words (`reveal`) also brings the mail text into view.
  const chooseLine = (lineId: string | null, reveal = false) => {
    setFocusLineId(lineId);
    onFocusedLineChange?.(detail.lines.find((line) => line.id === lineId) ?? null, reveal);
  };
  const focusLine = (lineId: string, highlight = false) => {
    // The row is what the person acts on next, so the row scrolls and the mail only marks.
    chooseLine(lineId);
    // The row is the <tr> in the table, the card's <li> in the list (`draft-focus.ts`).
    focusDraftRow(lineId, { highlight });
  };
  const pickLine = (lineId: string) => {
    // Opening the search writes nothing. The pick itself takes an unassigned case (the
    // server does), so a person who opens and cancels does not end up with the case.
    logger.debug("[case] open pick dialog", { case: detail.id, line: lineId });
    setPickLineId(lineId);
  };
  const openPrice = (lineId: string) => {
    logger.debug("[case] open price dialog", { case: detail.id, line: lineId });
    setPriceLineId(lineId);
  };
  // A click on a blocker sentence, in the bar or in "Open points (n)": go to its control.
  const goTo = (target: BlockerTarget) => {
    logger.debug("[case] go to", { case: detail.id, target: target.kind });
    switch (target.kind) {
      case "customer":
        setPickingCustomer(true);
        return;
      case "line":
        focusLine(target.lineId, true);
        return;
      case "quantity": {
        // A colleague has the case: no write on many lines at once. The sentence goes to
        // the first row without a quantity; one row can still be corrected.
        if (!colleagueHasCase(writes)) {
          setSettingQuantities(true);
          return;
        }
        const firstMissing = rows.find(
          (row) => row.line.qty == null && row.word !== LINE_STATUS_WORDS.noItem,
        );
        if (firstMissing) {
          focusLine(firstMissing.line.id, true);
        }
        return;
      }
      case "add_line":
        setAddingLine(true);
        return;
      case "checks":
        openSheet("checks");
        return;
      case "documents":
        openSheet("documents");
        return;
    }
  };

  // ─── the row keys: they act on the row of the draft that holds the focus ───
  const workOpen = (detail.workStatus ?? "open") === "open";
  /** The row with the focus, when the case is open and the row has its menu; else null. */
  const currentRow = (lineId?: string | null): DraftRow | null => {
    const id = lineId ?? currentDraftLineId();
    const row = id ? rows.find((candidate) => candidate.line.id === id) : undefined;
    return row && workOpen && hasLineMenu({ detail }, row) ? row : null;
  };
  /**
   * A key decided this row: the next row that needs the person takes the focus, so the
   * same key works down the list. Without one the decided row keeps the focus.
   */
  const moveOnFrom = (row: DraftRow) => {
    const next = nextRowThatNeedsYou(rows, row.line.id);
    if (next) {
      focusNextRowAfterDecision(next.line.id, row.line.id);
    } else {
      returnFocusToRow(row.line.id);
    }
  };
  /** Enter: confirm a suggestion, or open the item search of a row without an item. */
  const confirmRow = (lineId?: string | null): boolean => {
    const row = currentRow(lineId);
    if (!row || row.line.decision) {
      return false;
    }
    if (row.word === LINE_STATUS_WORDS.confirm && row.line.matchedItemId) {
      writes.decideLine(row.lineIndex, row.line.lineKey ?? "", { kind: "confirm" });
      moveOnFrom(row);
      return true;
    }
    if (row.word === LINE_STATUS_WORDS.pick) {
      pickLine(row.line.id);
      return true;
    }
    return false;
  };
  const rowKeys = FEATURES.shortcuts && workOpen && rows.length > 0;
  useShortcutHandler("rowConfirm", rowKeys ? () => confirmRow() : null);
  useShortcutHandler(
    "rowNoItem",
    rowKeys
      ? () => {
          const row = currentRow();
          if (!row || row.line.decision) {
            return false;
          }
          writes.decideLine(row.lineIndex, row.line.lineKey ?? "", { kind: "no_item" });
          moveOnFrom(row);
          return true;
        }
      : null,
  );
  useShortcutHandler(
    "rowPrice",
    rowKeys
      ? () => {
          const row = currentRow();
          // A price needs an item, as in the row menu.
          if (!row || !effectiveItem(row.line).item) {
            return false;
          }
          openPrice(row.line.id);
          return true;
        }
      : null,
  );
  useShortcutHandler(
    "rowQuantity",
    rowKeys
      ? () => {
          const row = currentRow();
          const cell = row
            ? draftRowElement(row.line.id)?.querySelector<HTMLElement>(`[${QTY_EDIT_ATTR}] button`)
            : null;
          if (!cell) {
            return false;
          }
          cell.click(); // The cell opens its field with the old value selected.
          return true;
        }
      : null,
  );
  useShortcutHandler(
    "rowPick",
    rowKeys
      ? () => {
          const row = currentRow();
          if (!row || row.line.decision) {
            return false;
          }
          pickLine(row.line.id);
          return true;
        }
      : null,
  );

  const pickingLine = detail.lines.find((line) => line.id === pickLineId) ?? null;
  const pickingIndex = pickingLine ? detail.lines.findIndex((l) => l.id === pickingLine.id) : -1;
  // "Set price" from the row menu: the write itself takes an unassigned case first.
  const pricingLine = detail.lines.find((line) => line.id === priceLineId) ?? null;
  const pricingIndex = pricingLine ? detail.lines.findIndex((l) => l.id === pricingLine.id) : -1;
  // "Correct quantity" from the row menu (U-01): the same dialog as the line details.
  const qtyLine = detail.lines.find((line) => line.id === qtyLineId) ?? null;
  const qtyIndex = qtyLine ? detail.lines.findIndex((l) => l.id === qtyLine.id) : -1;
  // A pick that replaces a certain customer says so in a toast (round 4); the first
  // confirmation keeps its "being matched" sentence under the customer line.
  const pickCustomer = (account: string, name?: string) => {
    const replacing = customer.state === "certain";
    writes.confirmCustomer(
      account,
      () => {
        setPickingCustomer(false);
        if (replacing) {
          toast.add({
            title: "Customer changed",
            description: customerChangedText(name || account),
            type: "success",
          });
        }
      },
      // A replaced customer has its own toast; a first confirmation gets "Customer confirmed".
      { quiet: replacing },
    );
  };
  const detailsCount = detailsCountOf(detail, openQuestionsOf(draft).length);

  return (
    // On the page the work column adds no box of its own (`contents`): the sticky action
    // bar can then rise to the viewport bottom from the mail card on. Inside a box that
    // starts under a tall mail card, the bar sat below the fold at 390 and 1366 px
    // (measured 12 Oct 2026, tester round 5). The pane keeps its own column.
    <div
      className={cn(variant === "page" ? "contents" : "flex min-w-0 flex-col gap-4", className)}
      data-case-work={variant}
    >
      {/* On the page the customer line sits in the mail card, with the sender. */}
      {variant === "pane" && !outOfScope && (
        <>
          <CustomerLine detail={detail} writes={writes} onFind={() => setPickingCustomer(true)} />
          <CustomerParties detail={detail} />
        </>
      )}
      {detail.engineError && (
        <Alert tone="neutral" title="Not processed">
          {detail.engineError}
        </Alert>
      )}
      {/* "Look again" for the case, the state while the request waits, and the sentence
          of a revised quotation (the deeper search, round 2). */}
      {!outOfScope && <InvestigationBar detail={detail} writes={writes} />}
      <DraftLines
        detail={detail}
        lines={outOfScope ? [] : realLines}
        layout={layout}
        maySeeMargin={maySeeMargin}
        detailsOn={detailsOn}
        onDetailsChange={(on) => {
          track("case.details_switch", { on }, { caseId: detail.id });
          remember(DETAILS_KEY, on ? "on" : "off");
          setDetailsOn(on);
        }}
        focusLineId={focusLineId}
        writes={writes}
        mayApprove={mayApprove}
        onPick={pickLine}
        onSetPrice={openPrice}
        onCorrectQty={(lineId) => {
          logger.debug("[case] open line correction", { case: detail.id, line: lineId });
          setQtyLineId(lineId);
        }}
        onOpenDetails={(lineId) => {
          track("case.line_details", {}, { caseId: detail.id });
          chooseLine(lineId);
        }}
        onChooseLine={(lineId) => chooseLine(lineId, true)}
        onSetQuantityAll={() => setSettingQuantities(true)}
        onRowEnter={confirmRow}
      />
      <PrimaryAction
        detail={detail}
        variant={variant}
        lines={lines}
        customer={customer}
        flow={flow}
        writes={writes}
        draft={draft}
        draftRead={!eligible || !answer.isLoading}
        preparing={preparing}
        prepareError={prepareError}
        onFocusLine={focusLine}
        onFindCustomer={() => setPickingCustomer(true)}
        onOpenSheet={openSheet}
        onAddLine={() => setAddingLine(true)}
        onTarget={goTo}
        onSetQuantityAll={() => setSettingQuantities(true)}
        onSetPrice={openPrice}
        onNothing={onOpenInI2q}
        trailing={
          // "Details (n)" sits in the bar, at its end: under the pinned bar it was at
          // the very end of the page (ease round, 13 Oct 2026).
          <span className="ml-auto flex flex-wrap items-center gap-2" data-bar-trailing>
            {/* While a rule holds the case, Details opens on the checks: the held rule
                is what the person came to read (tester round 5, 12 Oct 2026: the sheet
                opened with Checks folded). */}
            <DetailsButton
              count={detailsCount}
              onClick={() => openSheet(heldReasonOf(detail) ? "checks" : null)}
            />
            {variant === "pane" && onOpenInI2q && (
              <Button
                size="sm"
                variant="ghost"
                tone="neutral"
                startIcon={<Icon icon={ExternalLink} size="xs" />}
                onClick={onOpenInI2q}
              >
                {ACTION_LABELS.openInI2q}
              </Button>
            )}
          </span>
        }
      />
      <CaseSheet
        detail={detail}
        draft={draft}
        foDraft={foDraft.data ?? null}
        variant={variant}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        section={sheetSection}
        onCheckAgain={() => void foDraft.refetch()}
        onApprovePrice={
          mayApprove
            ? (lineKey) => {
                const lineIndex = detail.lines.findIndex((line) => line.lineKey === lineKey);
                if (lineIndex >= 0) {
                  writes.approvePrice(lineIndex, true);
                }
              }
            : undefined
        }
      />

      {pickingCustomer && (
        <PickCustomerDialog
          open
          onOpenChange={setPickingCustomer}
          caseId={detail.id}
          busy={writes.busy}
          error={writes.error}
          onPick={pickCustomer}
          initialSearch={customerSearchStart(detail)}
          suggested={customer.state === "suggested" ? customer.options : []}
          caseAccounts={caseAccountsOf(
            detail,
            customer.state === "suggested" ? customer.options : [],
          )}
        />
      )}
      {/* "Pick item" for one line, keyed so each open starts with an empty search. */}
      {pickingLine && (
        <PickItemDialog
          key={`pick-${pickingLine.id}`}
          open
          onOpenChange={(next) => {
            if (!next) {
              setPickLineId(null);
              // Opened by the key `i` or Enter: the row takes the focus back.
              returnFocusToRow(pickingLine.id);
            }
          }}
          caseId={detail.id}
          lineIndex={pickingIndex}
          line={pickingLine}
          caseArea={detail.area ?? null}
          onPicked={(item) => writes.itemPicked(pickingIndex, pickingLine.lineKey ?? "", item)}
        />
      )}
      {/* "Set price" for one line, keyed so each open starts from that line's price. */}
      {pricingLine && (
        <SetPriceDialog
          key={`price-${pricingLine.id}`}
          open
          onOpenChange={(next) => {
            if (!next) {
              setPriceLineId(null);
              // Opened by the key `p`: the row takes the focus back.
              returnFocusToRow(pricingLine.id);
            }
          }}
          detail={detail}
          line={pricingLine}
          lineIndex={pricingIndex}
          writes={writes}
        />
      )}
      {/* "Correct quantity" for one line (U-01), keyed so each open starts from that line. */}
      {qtyLine && (
        <LineCorrectionDialog
          key={`qty-${qtyLine.id}`}
          caseId={detail.id}
          line={qtyLine}
          lineIndex={qtyIndex}
          position={rows.find((row) => row.line.id === qtyLine.id)?.position}
          unassigned={writes.unassigned}
          open
          onOpenChange={(next) => {
            if (!next) {
              setQtyLineId(null);
            }
          }}
        />
      )}
      {/* "Set quantity for all lines", keyed so each open starts with the lines of now. */}
      {settingQuantities && (
        <BulkQuantityDialog
          open
          onOpenChange={setSettingQuantities}
          caseId={detail.id}
          rows={rows}
          writes={writes}
        />
      )}
      {addingLine && <AddLineDialog caseId={detail.id} open onOpenChange={setAddingLine} />}
    </div>
  );
}
